import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import {
  Header,
  Page,
  Content,
  TabbedLayout,
  InfoCard,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Link,
  makeStyles,
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import {
  CreatePipelineDialog,
  DeployStateChip,
} from '@internal/backstage-plugin-sql-pipeline';
import { SqlConsoleContent } from '../sql-console/SqlConsolePage';

const useStyles = makeStyles(theme => ({
  headerCell: {
    fontWeight: 700,
    backgroundColor: theme.palette.background.default,
    whiteSpace: 'nowrap',
  },
  nameLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const PipelineListTab = () => {
  const classes = useStyles();
  // 생성은 scaffolder 페이지가 아니라 플러그인 다이얼로그가 한다 — 새 DAG 규약은
  // 파일 3 개를 만들고 날짜 식·DDL·task 키가 서로 맞물려야 해서 JSON Schema
  // 폼으로는 교차 검증을 표현할 수 없다.
  const [createOpen, setCreateOpen] = useState(false);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        // 배열 전체를 넘긴다 — 카탈로그 필터는 값이 배열이면 OR 로 해석한다.
        // 이전에는 `[0]`(본인 user ref) 하나만 봐서 **팀 그룹이 소유한
        // 파이프라인이 보이지 않았다.** Keycloak 전환으로 `ent` 에 group ref 가
        // 실리고 `spec.owner` 가 팀 그룹이 되었으므로 둘을 맞춰야 한다
        // (DPD-13827).
        const owners = identity.ownershipEntityRefs.length
          ? identity.ownershipEntityRefs
          : [identity.userEntityRef];
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': 'pipeline',
            'spec.owner': owners,
          },
        });
        setEntities(response.items);
      } catch {
        setEntities([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [catalogApi, identityApi]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          내 파이프라인 <Chip label={`${entities.length}개`} size="small" color="primary" />
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setCreateOpen(true)}
        >
          새 파이프라인 생성
        </Button>
      </Box>

      <CreatePipelineDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {entities.length === 0 ? (
        <InfoCard title="파이프라인 없음">
          <Typography variant="body2" color="textSecondary">
            등록된 파이프라인이 없습니다. "새 파이프라인 생성" 버튼을 눌러 첫 파이프라인을 만들어보세요.
          </Typography>
        </InfoCard>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>이름</TableCell>
                <TableCell className={classes.headerCell} style={{ minWidth: 360 }}>설명</TableCell>
                <TableCell className={classes.headerCell}>스케줄</TableCell>
                <TableCell className={classes.headerCell}>반영 상태</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entities.map(entity => {
                const name = entity.metadata.name;
                const desc = entity.metadata.description ?? '-';
                const schedule = (entity.spec as any)?.pipelineConfig?.schedule ?? '-';
                return (
                  <TableRow key={name} hover>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/catalog/default/component/${name}`}
                        className={classes.nameLink}
                      >
                        {name}
                      </Link>
                    </TableCell>
                    <TableCell>{desc}</TableCell>
                    <TableCell>
                      <code>{schedule}</code>
                    </TableCell>
                    <TableCell>
                      <DeployStateChip pipelineName={name} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export const DataHubPage = () => {
  return (
    <Page themeId="tool">
      <Header
        title="Data Hub"
        subtitle="파이프라인 관리와 SQL 쿼리를 한 곳에서"
      />
      <TabbedLayout>
        <TabbedLayout.Route path="/" title="Pipelines">
          <Content>
            <PipelineListTab />
          </Content>
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/sql-console" title="SQL Console">
          <Content>
            <SqlConsoleContent />
          </Content>
        </TabbedLayout.Route>
      </TabbedLayout>
    </Page>
  );
};
